import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTokenFromLink } from '../../hooks/useTokenFromLink';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const inputStyle = {
  width: '100%',
  height: 48,
  padding: '0 14px',
  borderRadius: 10,
  border: '1px solid #e5e7eb',
  fontSize: 14,
  color: '#111827',
  backgroundColor: '#fff',
  outline: 'none',
  boxSizing: 'border-box',
};

export default function DoorlyProResetPassword() {
  const navigate = useNavigate();
  // 🔑 hooks/useTokenFromLink: read once, stripped from the address bar, no-referrer while the page is open.
  const token = useTokenFromLink();
  const [form, setForm] = useState({ password: '', confirm: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.password) { setError('Please enter a new password.'); return; }
    // The server's rule (POST /api/doorlypro/reset-password), the same as registration and change-password.
    if (form.password.length < 8) { setError('Password must be at least 8 characters.'); return; }
    if (form.password !== form.confirm) { setError('Passwords do not match.'); return; }
    if (!token) { setError('Invalid or missing reset token.'); return; }

    setLoading(true);
    setError('');
    try {
      await axios.post(`${API_URL}/api/doorlypro/reset-password`, {
        token,
        password: form.password,
      });
      navigate('/doorlypro/login', { state: { message: 'Password reset successfully. Please sign in.' } });
    } catch (err) {
      setError(err.response?.data?.error || 'Reset failed. The link may have expired.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/doorlypro/login" className="inline-block">
            <img
              src={`${process.env.PUBLIC_URL}/assets/DoorlyPro.png`}
              alt="DoorlyPro"
              style={{ height: 40, width: 'auto' }}
              onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'inline'; }}
            />
            <span style={{ display: 'none', fontSize: 24, fontWeight: 800, color: '#3a6ad9' }}>Doorly<span style={{ color: '#111827' }}>Pro</span></span>
          </Link>
          <h1 className="text-2xl font-bold text-black mt-6 mb-1">Set New Password</h1>
          <p className="text-gray-500 text-sm">Enter and confirm your new password below. At least 8 characters.</p>
        </div>

        <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-8">
          {error && (
            <div className="mb-5 px-4 py-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} noValidate>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1.5">New Password</label>
              <input
                name="password"
                type="password"
                value={form.password}
                onChange={handleChange}
                placeholder="New password"
                autoComplete="new-password"
                style={inputStyle}
                onFocus={(e) => (e.target.style.borderColor = '#3a6ad9')}
                onBlur={(e) => (e.target.style.borderColor = '#e5e7eb')}
              />
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Confirm New Password</label>
              <input
                name="confirm"
                type="password"
                value={form.confirm}
                onChange={handleChange}
                placeholder="Confirm new password"
                autoComplete="new-password"
                style={inputStyle}
                onFocus={(e) => (e.target.style.borderColor = '#3a6ad9')}
                onBlur={(e) => (e.target.style.borderColor = '#e5e7eb')}
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn-primary btn-full text-white hover:opacity-90 disabled:opacity-60"
              style={{ backgroundColor: '#3a6ad9' }}
            >
              {loading ? 'Resetting...' : (
                <>
                  <span>Reset Password</span>
                  <span className="btn-arrow">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9 5l7 7-7 7"/></svg>
                  </span>
                </>
              )}
            </button>
          </form>
        </div>

        <p className="text-center text-sm text-gray-500 mt-6">
          <Link to="/doorlypro/login" className="font-semibold" style={{ color: '#3a6ad9' }}>
            Back to Sign In
          </Link>
        </p>
      </div>
    </div>
  );
}
